<template>
    <div class="container-fluid content-wrapper">
        <div class="content-heading">Dashboard</div>
        <div v-show="isAdvancedReportingEnabled">
            <b-row class="mb-2" v-if="!isLoading && dashboardUri">
                <b-col>
                    <div class="d-flex gap-2 align-items-center">
                    <b-link class="btn btn-primary" :href="supersetDashboardsUri" target="_blank"><b-icon-bar-chart-fill></b-icon-bar-chart-fill>  Manage Dashboards</b-link>
                    <p-modal
                             ref="DashboardsModal"
                             label="Select Dashboard"
                             v-if="!isLoading"
                             button-label="Set Default Dashboard"
                             @show="initializeDefaultDashboardModal"
                             ok-only
                             ok-title="Close">
                        <p-table v-if="!isLoadingDashboards && dashboards.length"
                                 :items="dashboards"
                                 :fields='dashboardFields'
                                 :enable-row-selection="false"
                                 :pagination="false">
                            <template v-slot:cell(select_dashboard)='{ value, item }'>
                                <b-button size="sm" @click.prevent="setDashboardAsDefault(item)">Select</b-button>
                            </template>
                        </p-table>
                        <b-spinner v-else></b-spinner>
                    </p-modal>
                    </div>
                </b-col>
            </b-row>
            <b-row v-show="dashboardUri">
                <b-col>
                    <p-card>
                        <b-spinner v-if="isLoading"></b-spinner>
                        <div id="superset-dashboard" class="embedded-superset">No default dashboard configured</div>
                    </p-card>
                </b-col>
            </b-row>
        </div>
        <div v-if="!isLoading && (!dashboardUri || !isAdvancedReportingEnabled)">
            <p-card>
                <b-row>
                    <b-col>
                        <daily-sales />
                    </b-col>
                </b-row>
                <b-row>
                    <b-col>
                        <order-status />
                    </b-col>
                </b-row>
                <b-row cols="1" cols-md="2" cols-lg="3">
                    <b-col>
                        <term-widget ref="Term Widget" />
                    </b-col>
                    <b-col>
                        <transaction-widget ref="Recent Transactions" />
                    </b-col>
                    <b-col>
                    </b-col>
                </b-row>
            </p-card>
        </div>
    </div>
</template>
<script>

import axios from 'axios';
import TermWidget from '@/views/Dashboard/TermWidget.vue';
import TransactionWidget from '@/views/Dashboard/TransactionWidget.vue';
import DailySales from '@/views/Dashboard/DailySales.vue';
import OrderStatus from '@/views/Dashboard/OrderStatus.vue';

export default {
    components: {
        TermWidget,
        DailySales,
        OrderStatus,
        TransactionWidget
    },
    data() {
        return {
            dashboardUri: null,
            guestToken: null,
            isLoading: true,
            isLoadingDashboards: true,
            dashboards: [],
            dashboardFields: [
                {
                    key: 'select_dashboard',
                    label: ''
                },
                {
                    key: 'dashboard_title',
                    label: 'Dashboard'
                },
                {
                    key: 'status',
                    label: 'Status'
                }
            ]
        };
    },
    computed: {
        supersetDashboardsUri() {
            if (this.dashboardUri.endsWith('/')) {
                return `${this.dashboardUri}dashboard/list`;
            }

            return `${this.dashboardUri}/dashboard/list`;
        },
        isAdvancedReportingEnabled() {
            return this.$featureFlagEnabled('Report Generator');
        }
    },
    methods: {
        async fetchGuestToken(dashboardId) {
            const accessToken = await axios.get('dashboard/getGuestToken?dashboardId=' + dashboardId);
            this.guestToken = accessToken.data;
            return accessToken.data;
        },
        async initializeDefaultDashboardModal() {
            this.isLoadingDashboards = true;
            const result = await axios.get('dashboard/search');
            this.dashboards = Array.isArray(result.data)
                ? result.data
                : (result.data?.result ?? result.data?.items ?? []);
            this.isLoadingDashboards = false;
        },
        async setDashboardAsDefault(dashboard) {
            this.isLoading = true;
            this.$refs['DashboardsModal'].hide();
            const result = await axios.put('dashboard/setDefault', { dashboardId: dashboard.id });

            this.embedDashboard(result.data);

            this.isLoading = false;
        },
        embedDashboard(dashboardId) {
            window.supersetEmbeddedSdk.embedDashboard({
                id: dashboardId,
                supersetDomain: this.dashboardUri,
                mountPoint: document.getElementById("superset-dashboard"),
                fetchGuestToken: async () => await this.fetchGuestToken(dashboardId),
                dashboardUiConfig: {
                    hideTitle: false,
                    filters: {
                        expanded: false,
                    }
                },
                iframeSandboxExtras: ['allow-top-navigation', 'allow-popups-to-escape-sandbox']
            });
        }
    },
    async mounted() {
        const result = await axios.get('dashboard/getDashboardSettings');
        const dashboardSettings = result.data;
        const dashboardId = dashboardSettings.defaultDashboardId;
        this.dashboardUri = dashboardSettings.supersetUri;

        if (this.dashboardUri && dashboardId) {
            this.embedDashboard(dashboardId);
        }
        this.isLoading = false;
    }
};
</script>
<style scoped lang="scss">
@import '@/styles/app/common/variables.scss';
</style>
<style lang="scss">
    .embedded-superset iframe {
        border: 0;
        width: 100%;
        height: 100vh;
    }
</style>
